/**
 * Date utility functions for handling local timezone dates
 * 
 * These functions ensure dates are formatted using the browser's local timezone,
 * NOT UTC. This prevents bugs where dates shift to the wrong day due to timezone
 * conversion (e.g., 6pm PST on Dec 6 becoming Dec 7 in UTC).
 */

/**
 * Get date string in YYYY-MM-DD format using local timezone (not UTC)
 * @param {Date} date - The date to format (defaults to today)
 * @returns {string} Date string in YYYY-MM-DD format
 * 
 * @example
 * getLocalDateString() // "2025-12-06" (today in local timezone)
 * 
 * @example
 * const nextWeek = new Date();
 * nextWeek.setDate(nextWeek.getDate() + 7);
 * getLocalDateString(nextWeek) // "2025-12-13"
 */
export const getLocalDateString = (date = new Date()) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/**
 * Get today's date string in YYYY-MM-DD format using local timezone
 * @returns {string} Today's date in YYYY-MM-DD format
 * 
 * @example
 * getTodayLocalDateString() // "2025-12-06"
 */
export const getTodayLocalDateString = () => {
  return getLocalDateString();
};

/**
 * Get a date string with an offset from today in YYYY-MM-DD format using local timezone
 * @param {number} daysOffset - Number of days to offset from today (positive for future, negative for past)
 * @returns {string} Date string in YYYY-MM-DD format
 * 
 * @example
 * getDateWithOffset(7) // "2025-12-13" (7 days from now)
 * getDateWithOffset(-7) // "2025-11-29" (7 days ago)
 */
export const getDateWithOffset = (daysOffset = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return getLocalDateString(date);
};

